<script setup>
import TableCell from "@/components/global-themed/table/TableCell.vue";
import {ref, watch} from "vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ExpandableTableRow from "@/components/global-themed/table/ExpandableTableRow.vue";
import axios from "axios";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import useHealthcheckStore from "@/composables/backlinks/useHealthcheckStore";

const props = defineProps({
  backlink: {
    type: Object,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
});

const { activeTab } = useHealthcheckStore();

const showDetails = ref(props.isExpanded);

const emit = defineEmits(['toggle']);

watch(() => props.isExpanded, (newVal) => {
  showDetails.value = newVal;
});

const toggleDetails = () => {
  emit('toggle', props.backlink);
};

const queryClient = useQueryClient();

const {mutate: handleKeep, isPending: isKeepPending} = useMutation({
  mutationFn: async () =>  await axios.patch('/ajax/v2/backlinks/healthcheck/update', {
    backlink_to_keep: props.backlink.domain
  }),
  onSuccess: () => {
    queryClient.invalidateQueries(['healthcheck'])
  },
});

const {mutate: handleDisavow, isPending: isDisavowPending} = useMutation({
  mutationFn: async () =>  await axios.patch('/ajax/v2/backlinks/healthcheck/update', {
    backlink_to_disavow: props.backlink.domain
  }),
  onSuccess: () => {
    queryClient.invalidateQueries(['healthcheck'])
  },
});
</script>

<template>
  <ExpandableTableRow :hide-divider="showDetails">
    <TableCell
      col-span="8"
      class="tw-flex tw-space-x-4 tw-items-center tw-cursor-pointer"
      @click="toggleDetails"
    >
      <span class="tw-truncate tw-w-auto tw-block">{{ backlink.domain }}</span>
      <div
        v-tooltip="{
          content: `The authority score for this domain is ${backlink.score} out of 10. This is calculated from Trust Flow and Citation Flow`,
          placement: 'bottom',
          theme: 'dark-blue-tooltip',
        }"
        class="tw-rounded-lg tw-w-10 tw-inline-block tw-py-1 tw-font-semibold tw-text-lg tw-bg-blue tw-text-white tw-text-center"
      >
        {{ backlink.score }}
      </div>
      <svg-vue
        icon="icons/chevron"
        class="tw-h-3 tw-w-3 tw-text-grey tw-transition-transform duration-500"
        :class="{
          'tw-rotate-180': showDetails
        }"
      />
    </TableCell>

    <TableCell
      v-if="activeTab !== 'kept'"
      class="tw-flex tw-justify-end"
      :col-span="activeTab === 'disavowed' ? 4 : 2"
    >
      <DefaultButton
        variant="everest-bright"
        :disabled="isKeepPending || isDisavowPending"
        @click.prevent="handleKeep"
      >
        <svg-vue
          v-if="isKeepPending"
          icon="icons/loading"
          class="tw-w-5 tw-h-5 tw-mr-2"
        />
        <svg-vue
          v-else
          icon="icons/tick-thin"
          class="tw-w-5 tw-h-5 tw-mr-2"
        />
        <span class="tw-font-semibold tw-antialiased">Keep</span>
      </DefaultButton>
    </TableCell>

    <TableCell
      v-if="activeTab !== 'disavowed'"
      class="tw-flex"
      :class="{
        'tw-justify-start' : activeTab !== 'kept',
        'tw-justify-end' : activeTab === 'kept'
      }"
      :col-span="activeTab === 'kept' ? 4 : 2"
    >
      <DefaultButton
        variant="pink"
        :disabled="isDisavowPending || isKeepPending"
        @click.prevent="handleDisavow"
      >
        <svg-vue
          v-if="isDisavowPending"
          icon="icons/loading"
          class="tw-w-5 tw-h-5 tw-mr-2"
        />

        <svg-vue
          v-else
          icon="icons/cross"
          class="tw-w-5 tw-h-5 tw-mr-2"
        />
        <span class="tw-font-semibold tw-antialiased">Disavow</span>
      </DefaultButton>
    </TableCell>

    <template #accordion>
      <div
        v-show="showDetails"
        class="tw-transition-all tw-px-4 tw-pb-6 tw-space-y-4 tw-border tw-border-t-0"
      >
        <span class="tw-text-grey tw-text-base">
          Here are up to 10 example links from this domain:
        </span>

        <div
          v-for="(linkData, index) in backlink.links"
          :key="index"
          class="tw-flex tw-flex-col tw-border tw-border-blue tw-rounded-xl tw-px-8 tw-py-6 tw-gap-1"
        >
          <span class="tw-text-base tw-font-bold tw-text-grey-dark">
            {{ linkData.label }}
          </span>
          <a
            class="tw-text-blue tw-underline"
            target="_blank"
            :href="linkData.link"
          >
            {{ linkData.link }}
          </a>
        </div>
      </div>
    </template>
  </ExpandableTableRow>
</template>
